import React, { Suspense, useState } from 'react';
import type { ComponentType } from 'react';

import { PageLoading } from '../../core-components/theme/components/Screen/ScreenStates';
import { tabStorage } from '../../storage/deviceStorage';

const LoadingFallback = <PageLoading />;

const RELOAD_COOLDOWN_MS = 10_000;

const isChunkLoadError = (error: unknown): boolean => {
  const message = (error as { message?: unknown } | null)?.message;
  return (
    typeof message === 'string' &&
    /dynamically imported module|module script failed|importing a module|ChunkLoadError|Loading chunk .* failed|Failed to fetch/i.test(message)
  );
};

const lastReloadAt = (): number => Number(tabStorage.read('chunkReloadAt')) || 0;

const loadWithChunkRecovery = <T,>(load: () => Promise<{ default: T }>): Promise<{ default: T }> =>
  load().catch((error: unknown) => {
    const recentlyReloaded = Date.now() - lastReloadAt() < RELOAD_COOLDOWN_MS;
    if (isChunkLoadError(error) && !recentlyReloaded) {
      tabStorage.write('chunkReloadAt', String(Date.now()));
      window.location.reload();
      return new Promise<{ default: T }>(() => undefined);
    }
    throw error;
  });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PageComponent = ComponentType<any>;

export interface LazyPage {
  (props: React.ComponentProps<PageComponent>): JSX.Element;
  preload: () => Promise<void>;
}

function withLazyLoading(importFunc: () => Promise<{ default: PageComponent }>, fallback: React.ReactNode = LoadingFallback): LazyPage {
  let loaded: PageComponent | null = null;
  let loading: Promise<{ default: PageComponent }> | null = null;

  const load = (): Promise<{ default: PageComponent }> => {
    loading ??= loadWithChunkRecovery(importFunc).then(
      (module) => {
        loaded = module.default;
        return module;
      },
      (error: unknown) => {
        loading = null;
        current = React.lazy(load);
        throw error;
      },
    );
    return loading;
  };

  let current = React.lazy(load);

  const LazyLoaded = (props: React.ComponentProps<PageComponent>): JSX.Element => {
    const [Page] = useState(() => loaded ?? current);
    return (
      <Suspense fallback={fallback}>
        <Page {...props} />
      </Suspense>
    );
  };
  LazyLoaded.preload = (): Promise<void> => load().then(
    () => undefined,
    () => undefined,
  );
  return LazyLoaded;
}

export default withLazyLoading;
